import { useObservableState } from 'observable-hooks';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';

import { FertilizationPlanTabKey } from '..';
import Form, { SubmitType } from '../../../../components/form';
import Amount from '../../../../components/form/elements/amount';
import Button from '../../../../components/form/elements/button';
import Group from '../../../../components/form/elements/group';
import Hidden from '../../../../components/form/elements/hidden';
import Map from '../../../../components/form/elements/map';
import Link from '../../../../components/form/elements/link';
import Readonly from '../../../../components/form/elements/readonly';
import {
  emptySelectOption,
  Option,
} from '../../../../components/form/elements/select';
import Text from '../../../../components/form/elements/text';
import Watch, { WatchType } from '../../../../components/form/elements/watch';
import Classifier from '../../../../enums/classifier';
import ClassifierGroup from '../../../../enums/classifier-group';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';
import { Unit } from '../../../../enums/unit';
import { YearSeason } from '../../../../enums/year-season';
import ClassifierValue from '../../../../interfaces/classifierValue';
import ClassifierValueProperty from '../../../../interfaces/classifierValueProperty';
import FertilizationPlan, {
  FertilizationPlanNeeded,
} from '../../../../interfaces/fertilization-plan';
import Field from '../../../../interfaces/field';
import {
  CROP_FERTILIZATION_PLAN,
  CROP_FERTILIZATION_PLAN_EDIT,
  CROP_FERTILIZATION_PLAN_VIEW,
  CROP_FIELDS_AND_WORKS_INDEX,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { can, round, route, uuid } from '../../../../services/utils';
import { RootState } from '../../../../store/store';
import Balance from './balance';
import ExpandableGroup from './expandable-group';
import Fertilizers from './fertilizers';
import css from './item.module.css';
import Needed from './needed';
import OptionModal from '../../../../components/modals/option-modal';
import { Section } from '../../fields-and-works';
import { CropWorkType } from '../../../../enums/crop-work-types';

export enum FertilizationPlanFormMode {
  CREATE = 'create',
  EDIT = 'edit',
  VIEW = 'view',
}

interface Composition {
  s?: number;
  n: number;
  p2o5: number;
  k2o: number;
}

interface FertilizationPlanForm {
  field_id: string;
  field: string;
  area: number;
  area_max: number;
  crop: string;
  foregrowth_postharvest_quantity: number;
  foregrowth_postharvest_quantity_unit: Option;
  green_manure: Option;
  needed: FertilizationPlanNeeded;
  needed_other_n: number;
  needed_other_p2o5: number;
  needed_other_k2o: number;
  fertilizers: {
    [key: string]: {
      work_id: string;
      work_done: boolean;
      type: Option;
      fertilizer: Option;
      amount: number;
      amount_unit: Option;
      year_season: string;
      s: number;
      n: number;
      p2o5: number;
      k2o: number;
    };
  };
  needed_totals?: Composition;
  fertilizer_totals?: Composition;
  balance?: Composition;
  geometry: string;
  name: string;
  block_number: string;
  other_fields?: Field[];
  lad_number?: string;
}

export interface FertilizationPlanSubmit {
  field_id: string;
  area: number;
  fertilizers: {
    work_id: string;
    id: string;
    amount: number;
    year_season: string;
  }[];
  foregrowth_postharvest_quantity: number;
  green_manure_id: string;
  needed_other_n: number;
  needed_other_p2o5: number;
  needed_other_k2o: number;
}

const MAP_HEIGHT = 325;

export default function FertilizationPlanItem({
  mode,
}: {
  mode: FertilizationPlanFormMode;
}) {
  const { farmId, season, fieldId, id } = useParams<{
    farmId: string;
    season: string;
    fieldId: string;
    id: string;
  }>();
  const history = useHistory();

  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.fertilization-plan.plan.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const { fieldService, classifierService, fertilizationPlanService } =
    useService();

  const schema = yup.object({
    field_id: yup.string().uuid().required(),
    area: yup
      .number()
      .decimal()
      .moreThan(0, 'Field must be a positive number')
      .required(),
    foregrowth_postharvest_quantity: yup.number().decimal().nullable(),
    green_manure: yup
      .object({ value: yup.string().uuid().required() })
      .required(),
    needed_other_n: yup.number().nullable(),
    needed_other_p2o5: yup.number().nullable(),
    needed_other_k2o: yup.number().nullable(),
    fertilizers: yup.lazy((fertilizer) =>
      yup.object({
        ...Object.keys(fertilizer ?? {}).reduce((shape, key) => {
          shape[key] = yup.object({
            fertilizer: yup
              .object({ value: yup.string().uuid().required() })
              .required(),
            amount: yup
              .number()
              .decimal()
              .moreThan(0, 'Field must be a positive number')
              .required(),
            year_season: yup
              .string()
              .oneOf(Object.values(YearSeason))
              .required(),
          });
          return shape;
        }, {} as ObjectShape),
      })
    ),
  });

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const getPropertyValue = (
    properties: {
      [key: string]: ClassifierValueProperty;
    },
    key: string
  ) =>
    properties
      ? Object.values(properties).find((p) => p.property.key === key)?.value ??
        null
      : null;

  const calcComposition = (
    value: number,
    correction: number,
    amount: number
  ) => {
    if (!value || Number.isNaN(amount)) {
      return 0;
    } else {
      let result = (amount * value) / 100;
      if (correction && !Number.isNaN(correction)) {
        result = result * (correction / 100);
      }
      return round(result);
    }
  };

  const formatComposition = (value: number) => (value ? value : null);

  const cafComposition = (value: number, correction: number, amount: number) =>
    formatComposition(calcComposition(value, correction, amount));

  const fieldToForm = (
    field: Field,
    greenManure: ClassifierValue[],
    needed: FertilizationPlanNeeded
  ): FertilizationPlanForm => ({
    field_id: field.id,
    field: `${field.lad_number}, ${field.name}, ${field.area} ha`,
    area: field.area,
    area_max: field.area,
    lad_number: field.lad_number,
    geometry: field.geometry,
    name: field.name,
    block_number: field.block_number,
    crop: needed.needForPlannedHarvest.crop.label,
    foregrowth_postharvest_quantity: null,
    foregrowth_postharvest_quantity_unit: { value: Unit.TON, label: Unit.TON },
    green_manure: greenManure.length
      ? { value: greenManure[0].id, label: greenManure[0].value }
      : emptySelectOption,
    needed: needed,
    needed_other_n: 0,
    needed_other_p2o5: 0,
    needed_other_k2o: 0,
    fertilizers: {
      [uuid()]: {
        work_id: null,
        work_done: false,
        type: emptySelectOption,
        fertilizer: emptySelectOption,
        amount: null,
        amount_unit: { value: Unit.KILOGRAM, label: Unit.KILOGRAM },
        year_season: '',
        s: null,
        n: null,
        p2o5: null,
        k2o: null,
      },
    },
    other_fields: field.other_fields,
  });

  const planToForm = (plan: FertilizationPlan): FertilizationPlanForm => {
    let greenManureNCorrection = 0;
    if (plan.green_manure) {
      greenManureNCorrection = getPropertyValue(
        plan.green_manure.properties,
        ClassifierPropertyKey.N_CORRECTION
      );
    }
    const fertilizerEntries: {
      [key: string]: Composition & {
        work_id: string;
        work_done: boolean;
        type: { id: string; label: string };
        fertilizer: ClassifierValue;
        amount: number;
        year_season: string;
        n_correction: number;
        p2o5_correction: number;
        k2o_correction: number;
      };
    } = {};
    plan.works.forEach((work) => {
      const area = work.fields
        .filter((field) => field.id === plan.field_id)
        .reduce(
          (sum, field) =>
            sum + Number.parseFloat(field.pivot.treated_area.toString()),
          0
        );
      const totalArea = work.fields.reduce(
        (sum, field) =>
          sum + Number.parseFloat(field.pivot.treated_area.toString()),
        0
      );
      work.products
        .filter((workProduct) => workProduct.fertilizer)
        .forEach((workProduct) => {
          const fertilizer = workProduct.fertilizer;
          const manure = workProduct.manureY1;
          const key = work.id.concat('|', fertilizer.id);
          fertilizerEntries[key] = {
            work_id: work.id,
            work_done: work.done,
            type: getPropertyValue(
              fertilizer.properties,
              ClassifierPropertyKey.FERTILIZER_TYPE
            )[0],
            fertilizer: fertilizer,
            s: getPropertyValue(fertilizer.properties, ClassifierPropertyKey.S),
            n: getPropertyValue(fertilizer.properties, ClassifierPropertyKey.N),
            p2o5: getPropertyValue(
              fertilizer.properties,
              ClassifierPropertyKey.P2O5
            ),
            k2o: getPropertyValue(
              fertilizer.properties,
              ClassifierPropertyKey.K2O
            ),
            n_correction: getPropertyValue(
              manure?.properties,
              ClassifierPropertyKey.N
            ),
            p2o5_correction: getPropertyValue(
              manure?.properties,
              ClassifierPropertyKey.P2O5
            ),
            k2o_correction: getPropertyValue(
              manure?.properties,
              ClassifierPropertyKey.K2O
            ),
            amount:
              (fertilizerEntries[key]?.amount || 0) +
              workProduct.work_product_warehouses.reduce(
                (sum, warehouses) =>
                  sum +
                  round((warehouses.quantity * (area / totalArea)) / area, 2),
                0
              ),
            year_season: work.pivot.year_season,
          };
        });
    });

    const fertilizers = Object.fromEntries(
      Object.values(fertilizerEntries).map((entry) => [
        uuid(),
        {
          work_id: entry.work_id,
          work_done: entry.work_done,
          type: {
            value: entry.type.id,
            label: entry.type.label,
          },
          fertilizer: {
            value: entry.fertilizer.id,
            label: entry.fertilizer.value,
            s: entry.s,
            n: entry.n,
            p2o5: entry.p2o5,
            k2o: entry.k2o,
            n_correction: entry.n_correction,
            p2o5_correction: entry.p2o5_correction,
            k2o_correction: entry.k2o_correction,
          },
          amount: entry.amount,
          amount_unit: { value: Unit.KILOGRAM, label: Unit.KILOGRAM },
          year_season: entry.year_season,
          s: cafComposition(entry.s, null, entry.amount),
          n: cafComposition(entry.n, entry.n_correction, entry.amount),
          p2o5: cafComposition(entry.p2o5, entry.p2o5_correction, entry.amount),
          k2o: cafComposition(entry.k2o, entry.k2o_correction, entry.amount),
        },
      ])
    );

    return {
      field_id: plan.field_id,
      field: `${plan.field.lad_number}, ${plan.field.name}, ${plan.field.area} ha`,
      area: plan.area,
      area_max: plan.field.area,
      geometry: plan.field.geometry,
      lad_number: plan.field.lad_number,
      name: plan.field.name,
      block_number: plan.field.block_number,
      other_fields: plan?.other_fields,
      crop: plan.needed.needForPlannedHarvest.crop.label,
      fertilizers: fertilizers,
      foregrowth_postharvest_quantity: plan.foregrowth_postharvest_quantity,
      foregrowth_postharvest_quantity_unit: {
        value: Unit.TON,
        label: Unit.TON,
      },
      green_manure: plan.green_manure
        ? {
            value: plan.green_manure.id,
            label: plan.green_manure.value,
            nCorrection: greenManureNCorrection,
          }
        : emptySelectOption,
      needed: plan.needed,
      needed_other_n: plan.needed_other_n,
      needed_other_p2o5: plan.needed_other_p2o5,
      needed_other_k2o: plan.needed_other_k2o,
      needed_totals: {
        n: formatComposition(plan.needed_n),
        p2o5: formatComposition(plan.needed_p2o5),
        k2o: formatComposition(plan.needed_k2o),
      },
      fertilizer_totals: {
        s: formatComposition(plan.fertilizer_s),
        n: formatComposition(plan.fertilizer_n),
        p2o5: formatComposition(plan.fertilizer_p2o5),
        k2o: formatComposition(plan.fertilizer_k2o),
      },
      balance: {
        n: formatComposition(plan.balance_n),
        p2o5: formatComposition(plan.balance_p2o5),
        k2o: formatComposition(plan.balance_k2o),
      },
    };
  };

  const formToSubmit = (
    form: FertilizationPlanForm
  ): FertilizationPlanSubmit => ({
    field_id: form.field_id,
    area: form.area,
    fertilizers: Object.values(form.fertilizers).map((fertilizer) => ({
      work_id: fertilizer.work_id,
      id: fertilizer.fertilizer.value,
      amount: fertilizer.amount,
      year_season: fertilizer.year_season,
    })),
    foregrowth_postharvest_quantity: form.foregrowth_postharvest_quantity,
    green_manure_id: form.green_manure?.value,
    needed_other_n: form.needed_other_n,
    needed_other_p2o5: form.needed_other_p2o5,
    needed_other_k2o: form.needed_other_k2o,
  });

  const [values] = useObservableState<FertilizationPlanForm>(() => {
    if (mode === FertilizationPlanFormMode.CREATE) {
      return forkJoin([
        fieldService.show(farmId, fieldId),
        classifierService.values(
          ClassifierGroup.CROP,
          Classifier.CORRECTION_BY_GREEN_MANURE_TYPE,
          { [ClassifierPropertyKey.N_CORRECTION]: 0 }
        ),
        fertilizationPlanService.needed(farmId, season, fieldId),
      ]).pipe(
        map(([field, greenManure, needed]) =>
          fieldToForm(field, greenManure, needed)
        )
      );
    } else {
      return fertilizationPlanService
        .show(farmId, season, id)
        .pipe(map((plan) => planToForm(plan)));
    }
  }, null);

  const submit = useCallback<SubmitType<any>>(
    (form: FertilizationPlanForm) => [
      mode === FertilizationPlanFormMode.CREATE
        ? fertilizationPlanService.store(farmId, season, formToSubmit(form))
        : fertilizationPlanService.update(
            farmId,
            season,
            id,
            formToSubmit(form)
          ),
      (plan: FertilizationPlan) =>
        history.push(
          route(CROP_FERTILIZATION_PLAN_VIEW, {
            farmId: farmId,
            season: season,
            tab: FertilizationPlanTabKey.PLAN,
            id: plan.id,
          })
        ),
    ],
    [fertilizationPlanService, history, mode, farmId, id, season]
  );

  const deleteFertilizationPlanModal = useCallback(() => {
    return (
      <OptionModal
        title={tp('delete')}
        content={tp('confirm_delete')}
        buttons={[
          { title: t('yes'), name: 'yes', value: true },
          { title: t('no'), name: 'no', value: false },
        ]}
        onChoice={(choice) => {
          if (choice) {
            fertilizationPlanService.destroy(farmId, season, id).subscribe(
              () => {
                history.push(
                  route(CROP_FERTILIZATION_PLAN, {
                    farmId: farmId,
                    season: season,
                    tab: FertilizationPlanTabKey.PLAN,
                  })
                );
              },
              (error) => {
                if (error.status === 422) {
                  const url = route(
                    CROP_FIELDS_AND_WORKS_INDEX.concat(
                      '?type=:type&field_name=:field&done=true'
                    ),
                    {
                      farmId: farmId,
                      season: season,
                      tab: Section.workOverview,
                      type: CropWorkType.DISPERSION_OR_SPRAYING,
                      field: error.response,
                    }
                  );

                  setModal(
                    <OptionModal
                      title={tp('works-constraints-exist')}
                      content={tp('delete-works-first')}
                      buttons={[
                        { title: tp('goto-works'), name: 'yes', value: true },
                        { title: t('cancel'), name: 'no', value: false },
                      ]}
                      onChoice={(choice) => {
                        if (choice) {
                          history.push(url);
                          setModal(null);
                        } else {
                          setModal(null);
                        }
                      }}
                      onClose={() => setModal(null)}
                    />
                  );
                }
              }
            );
          } else {
            setModal(null);
          }
        }}
        onClose={() => setModal(null)}
      />
    );
  }, [t, tp, fertilizationPlanService, farmId, id, history, season]);

  const destroy = useCallback(() => {
    setModal(deleteFertilizationPlanModal);
  }, [deleteFertilizationPlanModal]);

  const updateComposition = (w: WatchType) => {
    const totals = {
      needed: w.getValues('needed_totals'),
      fertilizers: { s: 0, n: 0, p2o5: 0, k2o: 0 },
    };
    const fertilizers = w.getValues('fertilizers');
    Object.keys(fertilizers).forEach((id) => {
      const fertilizer = fertilizers[id].fertilizer ?? null;
      if (!fertilizer) {
        return;
      }
      const amount = fertilizers[id].amount ?? 0;
      const s = calcComposition(amount, null, fertilizer.s ?? 0);
      totals.fertilizers.s += s;
      const n = calcComposition(
        amount,
        fertilizer.n_correction,
        fertilizer.n ?? 0
      );
      totals.fertilizers.n += n;
      const p2o5 = calcComposition(
        amount,
        fertilizer.p2o5_correction,
        fertilizer.p2o5 ?? 0
      );
      totals.fertilizers.p2o5 += p2o5;
      const k2o = calcComposition(
        amount,
        fertilizer.k2o_correction,
        fertilizer.k2o ?? 0
      );
      totals.fertilizers.k2o += k2o;
      if (w.parentName === 'fertilizers.'.concat(id)) {
        w.setValue(w.parentName.concat('.s'), formatComposition(s));
        w.setValue(w.parentName.concat('.n'), formatComposition(n));
        w.setValue(w.parentName.concat('.p2o5'), formatComposition(p2o5));
        w.setValue(w.parentName.concat('.k2o'), formatComposition(k2o));
      }
    });
    w.setValue('fertilizer_totals.s', formatComposition(totals.fertilizers.s));
    w.setValue('fertilizer_totals.n', formatComposition(totals.fertilizers.n));
    w.setValue(
      'fertilizer_totals.p2o5',
      formatComposition(totals.fertilizers.p2o5)
    );
    w.setValue(
      'fertilizer_totals.k2o',
      formatComposition(totals.fertilizers.k2o)
    );
    w.setValue(
      'balance.s',
      formatComposition(totals.fertilizers.s - totals.needed?.s)
    );
    w.setValue(
      'balance.n',
      formatComposition(totals.fertilizers.n - totals.needed?.n)
    );
    w.setValue(
      'balance.p2o5',
      formatComposition(totals.fertilizers.p2o5 - totals.needed?.p2o5)
    );
    w.setValue(
      'balance.k2o',
      formatComposition(totals.fertilizers.k2o - totals.needed?.k2o)
    );
  };

  const compositionTitles = () => (
    <fieldset className={css['titles']}>
      <legend>
        <div>{tp('s')}</div>
        <div>{tp('n')}</div>
        <div>{tp('p2o5')}</div>
        <div>{tp('k2o')}</div>
      </legend>
    </fieldset>
  );

  const compositionTotals = (name: string) => ({
    s: Readonly(Text(name.concat('.s'), null, null, { inline: true })),
    n: Readonly(Text(name.concat('.n'), null, null, { inline: true })),
    p2o5: Readonly(Text(name.concat('.p2o5'), null, null, { inline: true })),
    k2o: Readonly(Text(name.concat('.k2o'), null, null, { inline: true })),
  });

  const [modal, setModal] = useState(null);

  const [body] = useState(() => [
    Group(
      [
        Group(
          [
            Hidden('field_id'),
            Readonly(Text('field', tp('field'), null, null, true)),
            Readonly(Text('crop', tp('crop'), null, null, true)),
            Watch(
              Amount(
                { amount: 'area', unit: 'area_unit' },
                {
                  label: tp('area'),
                  onDisabled: 'both',
                  disableUnit: true,
                  options: [{ label: 'ha' }],
                },
                true
              ),
              (watchType) => {
                const max = Number.parseFloat(watchType.getValues('area_max'));
                const area = Number.parseFloat(watchType.value);

                //otherwise couldn't delete all values in input
                if (Number.isNaN(area)) {
                  return;
                }

                if (area < 0 || area > max) {
                  watchType.setValue('area', max);
                }
              }
            ),
          ],
          null,
          false,
          true
        ),
      ],
      null,
      false,
      false,
      true,
      css['form_body_top']
    ),
    Map('default', 'other_fields', MAP_HEIGHT, true),
    compositionTitles,
    ExpandableGroup(
      'needed',
      [Needed(updateComposition)],
      compositionTotals('needed_totals'),
      tp('needed_fertilizer')
    ),
    ExpandableGroup(
      'fertilizer',
      [Fertilizers(updateComposition)],
      compositionTotals('fertilizer_totals'),
      tp('fertilizers'),
      true
    ),
    Balance(compositionTotals('balance'), tp('balance')),
  ]);

  const [footer] = useState(() => {
    const buttons = [];

    switch (mode) {
      case FertilizationPlanFormMode.CREATE:
        buttons.push(
          Link(
            route(CROP_FERTILIZATION_PLAN, {
              farmId: farmId,
              season: season,
              tab: FertilizationPlanTabKey.PLAN,
            }),
            'cancel'
          )
        );
        can(farmUser, { title: 'create fertilization_plan' }) &&
          buttons.push(Button('create'));
        break;
      case FertilizationPlanFormMode.EDIT:
        buttons.push(
          Link(
            route(CROP_FERTILIZATION_PLAN_VIEW, {
              farmId: farmId,
              season: season,
              id: id,
            }),
            'cancel'
          )
        );
        can(farmUser, { title: 'update fertilization_plan' }) &&
          buttons.push(Button('save'));
        break;
      case FertilizationPlanFormMode.VIEW:
        buttons.push(
          Link(
            route(CROP_FERTILIZATION_PLAN, {
              farmId: farmId,
              season: season,
              tab: FertilizationPlanTabKey.PLAN,
            }),
            'cancel'
          )
        );
        can(farmUser, { title: 'update fertilization_plan' }) &&
          buttons.push(
            Link(
              route(CROP_FERTILIZATION_PLAN_EDIT, {
                farmId: farmId,
                season: season,
                id: id,
              }),
              'edit',
              true
            )
          );
        can(farmUser, { title: 'delete fertilization_plan' }) &&
          buttons.push(Button('delete', destroy));
        break;
    }
    return buttons;
  });

  return (
    <>
      <Form
        className={css['form']}
        title={tp(mode)}
        mode={mode}
        body={body}
        footer={footer}
        values={values}
        submit={submit}
        schema={schema}
      />
      {modal}
    </>
  );
}
